/**
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatObj = require('../types/NatObj')
const NatCal = require('../types/NatCal')
const Complexe = require('../types/Complexe')
const Fonte = require('../types/Fonte')
const Pointeur = require('../types/Pointeur')
const StyleEncadrement = require('../types/StyleEncadrement')
const kernel = require('../kernel/kernel')
const CAffLiePt = require('./CAffLiePt')
const CCommentaire = require('./CCommentaire')
const CListeObjets = require('./CListeObjets')
const Nat = require('../types/Nat')
module.exports = CLatex

/**
 * Classe représentant un affichage LaTeX sur la figure.
 * @constructor
 * @extends CCommentaire
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la cosntruction ppropriétaire.
 * @param {Boolean} estElementFinal : true si l'objet est un élément final de construction.
 * @param {mtg32.Color} couleur : La coileur d'écriture
 * @param {Float} x_nom : L'abscisse d'affichage.
 * @param {Float} y_nom : L'ordonnée d'affichage.
 * @param {Integer} dec_x : Décalage horizontal d'affichage.
 * @param {Integer} dec_y : Décalage vertical d'affichage.
 * @param {Boolean} masque : true si l'objet est masqué.
 * @param {CPt} pointLie : null si l'affichage est libre ou pointe sur le point auquel il est lié.
 * @param {Integer} taillePolice : Donne la taille de la police utilisée.
 * @param {Boolean} encadrement : true si l'affichage est encadré.
 * @param {Boolean} effacementFond : true si le fond doit être effacé avant écritrue.
 * @param {mtg32.Color} couleurFond : La couleur de fond.
 * @param {Integer} alignementHorizontal : 0 pour alignement gauche, 1 pour centre, 2 pour droite.
 * @param {Integer} alignementVertical : 0 pour alignement vers le haut, 1 pour centré, 2 pour bas.
 * @param {String} chaineCommentaire : Contient le code LaTeX.
 * @param {CValeurAngle} angText : L'angle du texte par rapport à l'horizontale
 * @returns {CLatex}
 */
function CLatex (listeProprietaire, impProto, estElementFinal, couleur, x_nom, y_nom,
    dec_x, dec_y, masque, pointLie, taillePolice, encadrement, effacementFond, couleurFond, alignementHorizontal,
    alignementVertical, chaineCommentaire, angText) {
  if (arguments.length === 1) CCommentaire.call(this, listeProprietaire);
  else {
    CCommentaire.call(this, listeProprietaire, impProto, estElementFinal, couleur, x_nom, y_nom,
      dec_x, dec_y, masque, pointLie, taillePolice, encadrement, effacementFond, couleurFond, alignementHorizontal,
        alignementVertical, chaineCommentaire, angText);
    this.chaineCommentaire = chaineCommentaire;
  }
  // Spécial JavaScript : chaineAffichee = contiendra la chaîne utilisée lors de l'affichage précédent.
  // Si c'est la même, update ne fera pas appel à createg mais on changera simplement des coordonnées d'affichage du composant.
  this.chaineAffichee = "";
  this.div = null;
  // Ligne suivante rajoutée pour la version WebPack
}
CLatex.prototype = new CCommentaire();
CLatex.prototype.constructor = CLatex;
CLatex.prototype.superClass = "CCommentaire";
CLatex.prototype.className = "CLatex";

CLatex.errorVal ="Val Error";
CLatex.errorFrac ="Frac Error";
CLatex.errorIf ="If Error";
CLatex.errorFor ="For Error";
CLatex.errorCalc ="Calc Error";
CLatex.errorCalcSimp ="CalcSimp Error";
CLatex.errorForSimp ="ForSimp Error";


CLatex.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.pointLie);
  var ind2 = listeSource.indexOf(this.impProto);
  var angTextClone = this.angText.getClone(listeSource, listeCible);
  var ptelb = new CLatex(listeCible, listeCible.get(ind2, "CImplementationProto"),
    this.estElementFinal, this.couleur, this.x_nom, this.y_nom, this.dec_x, this.dec_y, this.masque,
    listeCible.get(ind1, "CPt"), this.taillePolice, this.encadrement,
    this.effacementFond, this.couleurFond, this.alignementHorizontal,
    this.alignementVertical, this.chaineCommentaire, angTextClone);
  ptelb.listeValeursDynamiquesUtilisees = new CListeObjets(listeCible.uniteAngle, listeCible.pointeurLongueurUnite);
  if (listeCible.className !== "CPrototype") ptelb.determineDependances();
  // Ligne suivante nécessaire car utilisé pour les exportations tikz
  ptelb.id = this.id;
  return ptelb;
};

CLatex.prototype.getNature = function() {
  return NatObj.NLatex;
};

CLatex.prototype.setClone = function(ptel) {
  CAffLiePt.prototype.setClone.call(this, ptel);
  this.chaineLatex = ptel.chaineLatex;
};

CLatex.prototype.rendChaineAffichage = function() {
  return this.chaineLatex;
};

/**
 * Fonction déterminant quels sont les calculs ou fonctions dont le commentaire dépend de façon dynamique.
 * @param indiceReel Si présent c'est qu'on est en train d'utiliser un commentaire
 * dans une boîte de dialogue d'aperçu et c'est alors l'indice du vrai commentaire qu'on est en train d'éditer
 */
CLatex.prototype.determineDependances = function(indiceReel = -1) {
  var chaineAAnalyser,indicecommentaire,len,jdeb,j,indparf,ch,st,nomvaleur,pValeur,nomvaleur2,pValeur2;
  chaineAAnalyser = this.chaineCommentaire.replace(/ /g,"");

  this.listeValeursDynamiquesUtilisees.retireTout();
  // Quand on recherche si c'est bien le nom d'une valeur valide
  // Il ne faut pas que la valeur ait été définie après l'affichage de valeur
  if (indiceReel !== -1) indicecommentaire = indiceReel
  else indicecommentaire = this.listeProprietaire.indexOf(this);
  if (indicecommentaire === -1) indicecommentaire = this.listeProprietaire.longueur() - 1;
  // On examine la chaine à la recherche de #Val(
  len = chaineAAnalyser.length;
  j = 0;
  while (((jdeb = chaineAAnalyser.indexOf("\\Val{",j)) !== -1) && j < len) {
    // On recherche la parenthèse fermante correspondante
    indparf = chaineAAnalyser.indexOf('}',jdeb + 5);
    if (indparf === -1) break;
    else {
      // On crée une chaine formé de ce qu'il y a entre \Val( et la parenthèse fermante
      ch = chaineAAnalyser.substring(jdeb+5, indparf).trim();
      // On retire les éventuels espaces de cete chaine
      // On sépare cette chaine avec les virgules
      st = ch.split(/\s*,\s*/);
      if (st.length === 0) j = indparf + 1;
      else {
        nomvaleur = st[0];
        // On recherche si c'est bien le nom d'une valeur valide
        // Il ne faut pas que la valeur ait été définie après l'affichage de valeur
        // Très important : quand determineDependances est appelé
        // par getClone() de CCommentaire, le commentaire n'a pas encoré été rajouté
        // à la liste clone et ainsi indiceCommentaire renvoie -1.
        // dans ce cas, indiceCommentaire doit valoir le nombre d'éléments actuels
        // de la liste clone qui le possède
        // Modifié version 4.7.2
        // CValDyn pValeur = pointeurValeurOuFonction(nomvaleur,indicecommentaire);
        pValeur = this.pointeur(NatCal.NTteValPourComDynSaufFonc,nomvaleur,indicecommentaire);
        if (pValeur === null) j = indparf + 1;
        else {
          this.listeValeursDynamiquesUtilisees.add(pValeur);
          j = indparf + 1;
        }
      }
    }
  }
  j=0;
  while (((jdeb = chaineAAnalyser.indexOf("\\For{",j)) !== -1) && j < len) {
    // On recherche la parenthèse fermante correspondante
    indparf = chaineAAnalyser.indexOf('}',jdeb + 5);
    if (indparf === -1) break;
    else {
      // On crée une chaine formé de ce qu'il y a entre \Val( et la parenthèse fermante
      ch = chaineAAnalyser.substring(jdeb+5, indparf).trim();
      // On sépare cette chaine avec les virgules
      st = ch.split(/\s*,\s*/);
      if (st.length === 0) j = indparf + 1;
      else {
        nomvaleur = st[0];
        pValeur = this.pointeur(NatCal.NTteValPourComDyn,nomvaleur,indicecommentaire);
        if (pValeur === null) j = indparf + 1;
        else {
          this.listeValeursDynamiquesUtilisees.add(pValeur);
          // Si la valeur n'existe pas, on ne l'inclut pas dans la chaîne à afficher
          j = indparf + 1;
        }
      }
    }
  }
  j=0;
  while (((jdeb = chaineAAnalyser.indexOf("\\Calc{",j)) !== -1) && j < len) {
    // On recherche la parenthèse fermante correspondante
    indparf = chaineAAnalyser.indexOf('}',jdeb + 6);
    if (indparf === -1) break;
    else {
      // On crée une chaine formé de ce qu'il y a entre \Val( et la parenthèse fermante
      ch = chaineAAnalyser.substring(jdeb+6, indparf).trim();
      // On sépare cette chaine avec les virgules
      st = ch.split(/\s*,\s*/);
      if (st.length === 0) j = indparf + 1;
      else {
        nomvaleur = st[0];
        pValeur = this.pointeur(NatCal.NTteValPourComDyn,nomvaleur,indicecommentaire);
        if (pValeur === null) j = indparf + 1;
        else {
          this.listeValeursDynamiquesUtilisees.add(pValeur);
          j = indparf + 1;
        }
      }
    }
  }
  j=0;
  while (((jdeb = chaineAAnalyser.indexOf("\\ForSimp{",j)) !== -1) && j < len) {
    // On recherche la parenthèse fermante correspondante
    indparf = chaineAAnalyser.indexOf('}',jdeb + 9);
    if (indparf === -1) break;
    else {
      // On crée une chaine formé de ce qu'il y a entre \Val( et la parenthèse fermante
      ch = chaineAAnalyser.substring(jdeb+9, indparf).trim();
      // On sépare cette chaine avec les virgules
      st = ch.split(/\s*,\s*/);
      if (st.length === 0) j = indparf + 1;
      else {
        nomvaleur = st[0];
        pValeur = this.pointeur(NatCal.NTteValPourComDyn, nomvaleur,indicecommentaire);
        if (pValeur === null) j = indparf + 1;
        else {
          this.listeValeursDynamiquesUtilisees.add(pValeur);
          // Si la valeur n'existe pas, on ne l'inclut pas dans la chaîne à afficher
          j = indparf + 1;
        }
      }
    }
  }
  j=0;
  while (((jdeb = chaineAAnalyser.indexOf("\\CalcSimp{",j)) !== -1) && j < len) {
    // On recherche la parenthèse fermante correspondante
    indparf = chaineAAnalyser.indexOf('}',jdeb + 10);
    if (indparf === -1) break;
    else {
      // On crée une chaine formé de ce qu'il y a entre \Val( et la parenthèse fermante
      ch = chaineAAnalyser.substring(jdeb+10, indparf).trim();
      st = ch.split(/\s*,\s*/);
      if (st.length === 0) j = indparf + 1;
      else {
        nomvaleur = st[0];
        pValeur = this.pointeur(NatCal.NTteValPourComDyn, nomvaleur,indicecommentaire);
        if (pValeur === null) j = indparf + 1;
        else {
          this.listeValeursDynamiquesUtilisees.add(pValeur);
          // Si la valeur n'existe pas, on ne l'inclut pas dans la chaîne à afficher
          j = indparf + 1;
        }
      }
    }
  }
  j = 0;
  while (((jdeb = chaineAAnalyser.indexOf("\\FracRed{",j)) !== -1) && j < len) {
    // On recherche la parenthèse fermante correspondante
    indparf = chaineAAnalyser.indexOf('}',jdeb + 9);
    if (indparf === -1) break;
    else {
      // On crée une chaine formé de ce qu'il y a entre \FracRed{ et la parenthèse fermante
      ch = chaineAAnalyser.substring(jdeb+9, indparf).trim();
      st = ch.split(/\s*,\s*/);
      if (st.length === 0) j = indparf + 1;
      else {
        nomvaleur = st[0];
        pValeur = this.pointeur(NatCal.NTteValRPourComDyn, nomvaleur,indicecommentaire);
        if (pValeur === null) j = indparf + 1;
        else {
          if (st.length === 1) j = indparf + 1;
          else {
            nomvaleur2 = st[1];
            pValeur2 = this.pointeur(NatCal.NTteValRPourComDyn,nomvaleur2,indicecommentaire);
            if (pValeur !== null) {
              this.listeValeursDynamiquesUtilisees.add(pValeur);
              this.listeValeursDynamiquesUtilisees.add(pValeur2);
            }
            j = indparf + 1;
          }
        }
      }
    }
  }
  j = 0;
  while (((jdeb = chaineAAnalyser.indexOf("\\If{",j)) !== -1) && j < len) {
    // On recherche la parenthèse fermante correspondante
    indparf = chaineAAnalyser.indexOf('}',jdeb + 4);
    if (indparf === -1) break;
    else {
      // On crée une chaine formée de ce qu'il y a entre \if{ et la parenthèse fermante
      ch = chaineAAnalyser.substring(jdeb+4, indparf).trim();
      pValeur = this.pointeur(NatCal.NTteValPourComDynSaufFonc, ch,indicecommentaire);
      if (pValeur === null) j = indparf + 1;
      else {
        this.listeValeursDynamiquesUtilisees.add(pValeur);
        j = indparf + 1;
      }
    }
  }
};

CLatex.prototype.positionne = function(infoRandom, dimf) {
  // this.chaineLatex = this.chaineCommentaire;
  if (this.listeValeursDynamiquesUtilisees.longueur() !== 0) {
    var ch = this.traiteCodesLatex(this.chaineCommentaire);
    this.isToBeUpdated = (ch !== this.chaineLatex);
    this.chaineLatex = ch;
  }
  else {
    this.chaineLatex = this.chaineCommentaire;
    this.isToBeUpdated = false;
  }
  CAffLiePt.prototype.positionne.call(this,infoRandom, dimf);
};

/**
 * Fonction préparant l'affichage par MathJax en créant un div provisoire
 * @param {boolean} bMemeMasque : Si true la prépartion de fait même si l'affichage est caché (sert dans
 * la boîte de dialogue de protocole)
 */
CLatex.prototype.setReady4MathJax = function(bMemeMasque) {
  // Modifié version 5.0.4 pour tenir compte des objts dupliqués d'un CLaTeX
  // qui est masqué
  var memeMasque = arguments.length === 0 ? false : bMemeMasque;
  if (!this.masque || this.hasDuplicate || memeMasque) {
    this.div = kernel.ce("div",{
      // style : this.styleLatex,
      style : this.getStyleLatex(),
      id : this.id+"prov",
      class : "text2jax_ignore"
    });
    var ch  = "$$"+ this.prelatex() + this.chaineLatex+"$$";
    this.div.appendChild(document.createTextNode(ch));
    document.body.appendChild(this.div);
    MathJax.Hub.Queue(["Typeset",MathJax.Hub,this.div]);
  }
};

CLatex.prototype.createg = function() {
  var w,h;
  var g = kernel.cens("g");
  try { // Modifié version mtgApp
    var c1 = this.div.childNodes[1];
    if (c1 === undefined) {
      this.deleteDiv();
      return g;
    }
    var c2 = c1.childNodes[0];
    if (c2 === undefined) {
      this.deleteDiv();
      return g;
    }
    var s = c2.childNodes[0];
    if (s === undefined) {
      this.deleteDiv();
      return g;
    }
    // Pour gérer Chrome
    while(s.tagName == "SPAN") s = s.childNodes[0];
    // Le test suivant est du à la compatibilité avec l'explorer
    if ((s.clientWidth !== 0) && (s.clientHeight !== 0)) {
      w = s.clientWidth;
      h = s.clientHeight;
    }
    else {
      // Modifié version 5.0.1
      // var b = s.getBBox();
      // ratio = b.height/b.width;
      // w = this.div.clientWidth;
      // h = w*ratio;
      // Incompatible avec version 2.3 de MathJax
      // w = s.width.animVal.value;
      // h = s.height.animVal.value;
      w = c2.clientWidth;
      h = c2.clientHeight;

    }
    var t = Fonte.tailleLatex(this.taillePolice); // Modifié 5.0.2
    if (h < t) h = t;
    this.rectAff.width = w;
    this.rectAff.height = h;
    this.rectAff.x = 0;
    this.rectAff.y = 0;
    switch (this.alignementHorizontal) {
      case CAffLiePt.alignHorCent :
        this.decHor = this.rectAff.width/2;
        break;
      case CAffLiePt.alignHorRight :
        this.decHor = this.rectAff.width;
        break;
      default : this.decHor = 0;
    }
    this.rectAff.x -= this.decHor;
    switch (this.alignementVertical) {
      case CAffLiePt.alignVerCent :
        this.decVer = this.rectAff.height/2;
        break;
      case CAffLiePt.alignVerLow :
        this.decVer = this.rectAff.height + 2;
        break;
      default : this.decVer = 0;
    }
    this.rectAff.y -= this.decVer;
    this.rectAff.width += 4;
    this.rectAff.height += 4;

    var clone = s.cloneNode(true);
    clone.setAttribute("x", (this.rectAff.x).toString()+"px");
    clone.setAttribute("y", (this.rectAff.y).toString()+"px");
    clone.setAttribute("width", w+"px");
    clone.setAttribute("height",h+"px");
    clone.setAttribute("style","");
    g.appendChild(clone);
    if (this.effacementFond || (this.encadrement !== StyleEncadrement.Sans))
      this.creeRectangle(g, this.couleurFond);
    // document.body.removeChild(this.div);
    this.deleteDiv();
    g.setAttribute("pointer-events", "none");
    // Ajout version 5.0.1 pour compatibilité avec l'explorer qui ne clipe pas correctement
    // Supprimé version mtgApp car pour des lieux d'objets de LaTeX liés à un point lié le point lié peut être hors-fenêtre
    /*
    if ((this.pointLie !== null) && !this.pointLie.dansFenetre)
      g.setAttribute("visibility", "hidden");
    else g.setAttribute("visibility", "visible");
    */
    g.setAttribute("visibility", "visible");
    // Ajout version Version 6.0 //////////////////////////////////////////
    this.positionneAngText(g);
    /////////////////////////////////////////////////////////////////////////

    // Ajout version MtgApp pour pouvoir déplacer un CEditeurEquation
    if (this.owner !== null) this.g = g;

    //
    return g;
  }
  catch(e) {
    this.deleteDiv();
    return g; //Retourne un g vide en cas de problème
  }
};

CLatex.prototype.setReady4MathJaxUpdate = function() {
  if (this.isToBeUpdated || (this.g && (this.g.childNodes.length === 0))) this.setReady4MathJax();
};

CLatex.prototype.update = function(svg) {
   var oldg = this.g;
   if (!this.isToBeUpdated) {
     /*
    var brect = this.effacementFond || (this.encadrement !== StyleEncadrement.Sans);
    // Modification version 5.0.1
    var newx = this.x_nom + this.dec_x - this.decHor;
    var newy = this.y_nom + this.dec_y - this.decVer;
    if ((newx !== this.rectAff.x) || (newy !== this.rectAff.y)) {
      this.rectAff.x = newx;
      this.rectAff.y = newy;
      this.positionneAngText(oldg);
      // var s = oldg.childNodes[brect ? ((this.encadrement === StyleEncadrement.Simple) ?  1 : 2) : 0];
      var s = oldg.childNodes[oldg.childNodes.length-1];
      s.setAttribute("x", newx.toString()+"px");
      s.setAttribute("y", newy.toString()+"px");
      if (brect) this.remplaceRectangle(oldg, this.couleurFond);
      this.positionneAngText(this.g);
      // Supprimé version 5.0.1
      // svg.replaceChild(oldg, oldg); // Rajouté version 4.9.2 pour des pbs de raffraichissement sous Android Firefox
    }
    */
     this.positionneAngText(this.g);
  }
  else {
    var g = this.createg();
    svg.replaceChild(g, oldg);
    g.setAttribute("id",this.id);
    this.g = g;
  }
  // Ajout version 5.0.1
  if ((this.pointLie !== null) && !this.pointLie.dansFenetre)
    this.g.setAttribute("visibility", "hidden");
  else this.g.setAttribute("visibility", "visible");
};

CLatex.prototype.parametres = function(nbArg, ch, debut, fin) {
  var len,i,j,codePar,ind2,car,k;
  ind2 = -1;
  len = ch.length;
  var chret = new Array(nbArg);
  i = debut;
  for (j = 0; j < nbArg; j++ ) {
    if (i >= len) return null;
    // On saute les espaces et les retours chariot
    while (((ch.charAt(i) === ' ') || (ch.charAt(i) === '\n')) && (i < len - 1)) i++;
    if (ch.charAt(i) !== '{') {
      return null;
    }
    codePar = 1;
    ind2 = i + 1;
    while ((ind2 < len)) {
      var deb = ch.substring(ind2);
      // Correction version 6.3.0 : Il peut y avoir des \{ dans le code LaTeX pour certaines accolades
      if (deb.indexOf("\\left\\{") === 0) ind2 += 7;
      else {
        if (deb.indexOf("\\right\\{") === 0) ind2 += 8;
        else {
          car = ch.charAt(ind2);
          if (car === '{') codePar++;
          else if (car === '}') codePar--;
          if (codePar === 0) break; else ind2++;
        }
      }
    }
    if (codePar !== 0) return null;
    // On retire les éventuels espaces de fin
    k = ind2 - 1;
    while (ch.charAt(k) === ' ') k--;
    chret[j] = ch.substring(i+1, k+1);
    i = ind2 + 1;
  }
  fin.setValue(ind2);
  return chret;
};
/**
 * Fonction traitant les codes LaTeX spécifiques à MathGraph32.
 * Pour plus de renseignements sur ces codes spéciaux, voir l'aide en ligne dans MathGraph32 (appuyer sur F1).
 * @param {String} ch : La chaîne contenant les codes.
 * @returns {String}
 */
CLatex.prototype.traiteCodesLatex = function(ch) {
  var ideb,param,chremp,len;
  var fin = new Pointeur(0);
  // Les codes \If peuvent être imbriqués les uns dans les autres. Il faut les rechecher tant
  // qu'il y en a et les traiter depuis le début.
  while ((ideb = ch.indexOf("\\If", 0)) !== -1) {
    param = this.parametres(3, ch, ideb + 3, fin);
    if (param === null) {
      ch = ch.substring(0, ideb) + CLatex.errorIf;
      break;
    }
    else {
      chremp = this.traiteCodeIf(param);
      len = ch.length;
      if (fin.getValue() === len - 1) ch = ch.substring(0, ideb) + chremp;
      else ch = ch.substring(0, ideb) + chremp + ch.substring(fin.getValue() + 1, len);
    }
  }
  // Traitement des codes \Val
  while ((ideb = ch.indexOf("\\Val", 0)) !== -1) {
    param = this.parametres(1, ch, ideb + 4, fin);
    if (param === null) {
      ch = ch.substring(0, ideb) + CLatex.errorVal;
      break;
    }
    else {
      chremp = "";
      try {
        chremp = this.traiteCodeVal(param[0]);
        len = ch.length;
        if (fin.getValue() === len - 1) ch = ch.substring(0, ideb) + chremp;
        else ch = ch.substring(0, ideb) + chremp + ch.substring(fin.getValue() + 1, len);
      }
      catch (e) {
        ch = ch.substring(0, ideb) + CLatex.errorVal;
      }
    }
  }
  // Traitement des codes \FracRed
  while ((ideb = ch.indexOf("\\FracRed", 0)) !== -1) {
    param = this.parametres(1, ch, ideb + 8, fin);
    if (param === null) {
      ch = ch.substring(0, ideb) + CLatex.errorFrac;
      break;
    }
    chremp = this.traiteCodeFracRed(param[0]);
    len = ch.length;
    if (fin.getValue() === len - 1) ch = ch.substring(0, ideb) + chremp;
    else ch = ch.substring(0, ideb) + chremp + ch.substring(fin.getValue() + 1, len);
  }
  // Traitement des codes \ForSimp. A faire iméprativement avant les \For
  while ((ideb = ch.indexOf("\\ForSimp", 0)) !== -1) {
    param = this.parametres(1, ch, ideb + 8, fin);
    if (param === null) {
      ch = ch.substring(0, ideb) + CLatex.errorForSimp;
      break;
    }
    chremp = this.traiteCodeForSimp(param[0]);
    len = ch.length;
    if (fin.getValue() === len - 1) ch = ch.substring(0, ideb) + chremp;
    else ch = ch.substring(0, ideb) + chremp + ch.substring(fin.getValue() + 1, len);
  }
  // Traitement des codes \For
  while ((ideb = ch.indexOf("\\For", 0)) !== -1) {
    param = this.parametres(1, ch, ideb + 4, fin);
    if (param === null) {
      ch = ch.substring(0, ideb) + CLatex.errorFor;
      break;
    }
    chremp = this.traiteCodeFor(param[0]);
    len = ch.length;
    if (fin.getValue() === len - 1) ch = ch.substring(0, ideb) + chremp;
    else ch = ch.substring(0, ideb) + chremp + ch.substring(fin.getValue() + 1, len);
    ideb = fin.getValue() + 1;
  }
  // Traitement des codes \CalcSimp. A faire impérativement avant les \Calc
  while ((ideb = ch.indexOf("\\CalcSimp", 0)) !== -1) {
    param = this.parametres(1, ch, ideb + 9, fin);
    if (param === null) {
      ch = ch.substring(0, ideb) +CLatex. errorCalcSimp;
      break;
    }
    chremp = this.traiteCodeCalcSimp(param[0]);
    len = ch.length;
    if (fin.getValue() === len - 1) ch = ch.substring(0, ideb) + chremp;
    else ch = ch.substring(0, ideb) + chremp + ch.substring(fin.getValue() + 1, len);
  }
  // Traitement des codes \Calc
  while ((ideb = ch.indexOf("\\Calc", 0)) !== -1) {
    param = this.parametres(1, ch, ideb + 5, fin);
    if (param === null) {
      ch = ch.substring(0, ideb) + CLatex.errorCalc;
      break;
    }
    chremp = this.traiteCodeCalc(param[0]);
    len = ch.length;
    if (fin.getValue() === len - 1) ch = ch.substring(0, ideb) + chremp;
    else ch = ch.substring(0, ideb) + chremp + ch.substring(fin.getValue() + 1, len);
  }
  return ch;
};
/**
 * Fonction traitant les codes \Val permettant d'insérer la représentation décimale
 * d'un calcul réel ou complexe ou d'une variable dans l'affichage.
 * @param {Array} param : Tableau de chaînes contenant les codes.
 * @returns {String} : La chaîne traitée.
 */
CLatex.prototype.traiteCodeVal = function(param) {
  // On sépare cette chaine avec les virgules
  var st,nomvaleur,indfin,pValeur,chnbdec,nbdecimales,chplus,signePlusImpose,nombre;
  var chnombre = "";
  st = param.split(/\s*,\s*/);
  if (st.length !== 0) {
    nomvaleur = st[0];
    indfin = this.listeValeursDynamiquesUtilisees.longueur() - 1;
    pValeur = this.listeValeursDynamiquesUtilisees.pointeurParNatureCalcul
        (NatCal.NTteValPourComDynSaufFonc, nomvaleur,indfin);
    if (pValeur !== null) {
      // Si la valeur n'existe pas, on ne l'inclut pas dans la chaîne à afficher
      if (pValeur.existe) {
        if (st.length >= 2) {
          chnbdec = st[1];
          if (chnbdec === "+") {
            chnombre = pValeur.rendChaineValeurPourCommentaire(2);
            if (chnombre.indexOf("-") === 0) chnombre = " -" + chnombre.substring(1);
            else chnombre = " +" + chnombre;
          }
          else {
            // On retire les espaces de début
            chnbdec = chnbdec.trim();
            nbdecimales = parseInt(chnbdec);
            chnombre = pValeur.rendChaineValeurPourCommentaire(nbdecimales) + " ";
            // Si le troisième paramètre :
            // commence par + : on impose un signe + devant l'affichage si le nombre est positif ou nul
            // finit par un zéro : on impose un signe + devant l'affichage si le nombre est positif ou nul
            // et on n'affiche rien si le nombre est nul
            // finit par 1 : on n'affiche pas le nombre si le nombre est égal à 1 et on affiche un signe - sil est égal à -1
            if (st.length >= 3) {
              chplus = st[2];
              // On retire les éventuels espaces de début
              chplus = chplus.trim();
              signePlusImpose = chplus.indexOf("+") === 0 ;
              if (signePlusImpose) {
                if (chnombre.indexOf("-") === 0) chnombre = " -" + chnombre.substring(1);
                else chnombre = " +" + chnombre;
              }
              if (chplus.indexOf("0") === chplus.length - 1) {
                nombre = pValeur.rendValeur();
                if (nombre === 0) return " ";
              }
              else {
                nombre = pValeur.rendValeur();
                if (chplus.indexOf("1") === chplus.length - 1) {
                  if (nombre === 1) return signePlusImpose ? " + " : " ";
                  else if (nombre === -1) return " - ";
                }
              }
            }
          }
        }
        else
        {
          // Si le nombre de décimales n'est pas précisé, on en met deux par défaut
          return pValeur.rendChaineValeurPourCommentaire(2);
        }
      }
    }
  }
  else return CLatex.errorVal;
  return chnombre;
};
/**
 * Fonction traitant les codes \FracRed permettant d'insérer une fraction
 * qui est la fraction réduite représentant un quotient de deux entiers.
 * @param {Array} param : Tableau de chaînes contenant les codes..
 * @returns {String} : La chaîne traitée.
 */
CLatex.prototype.traiteCodeFracRed = function(param) {
  var st,nomvaleur1,indfin,pValeurNum,num,nomvaleur2,pValeurDen,den,numabs,denabs,signePlusImpose,chsig,g, num1,den1,positif;
  // On sépare cette chaine avec les virgules
  var res = " ";
  st = param.split(/\s*,\s*/);
  if (st.length !== 0) {
    nomvaleur1 = st[0];
    // On recherche si c'est bien le nom d'une valeur valide
    // Il ne faut pas que la valeur ait été définie après l'affichage de valeur
    // Si c'est le nom d'une valeur utilisée, elle est comprise dans la
    // liste listeValeursDynamiquesUtilisees
    indfin = this.listeValeursDynamiquesUtilisees.longueur() - 1;
    pValeurNum = this.listeValeursDynamiquesUtilisees.pointeurParNatureCalcul
        (NatCal.NTteValRPourComDyn,nomvaleur1,indfin);
    if (pValeurNum === null) return CLatex.errorFrac;
    if (pValeurNum.existe) {
      num = pValeurNum.rendValeur(false);
      if (st.length >= 2) {
        nomvaleur2 = st[1];
        pValeurDen = this.listeValeursDynamiquesUtilisees.pointeurParNatureCalcul
            (NatCal.NTteValRPourComDyn, nomvaleur2,indfin);
        if (pValeurDen === null) return CLatex.errorFrac;
        if (pValeurDen.existe) {
          den = pValeurDen.rendValeur();
          if (den === 0) return CLatex.errorFrac;
          numabs = Math.abs(num);
          denabs = Math.abs(den);
          if ((numabs !== Math.floor(numabs)) || (numabs > kernel.MAX_VALUE)
              || (denabs !== Math.floor(denabs)) || (denabs > kernel.MAX_VALUE) || (denabs === 0)) return CLatex.errorFrac;
          else {
            signePlusImpose = false;
            chsig = "";
            if (st.length >= 3) {
              chsig = st[2];
              if (chsig.indexOf("+") === 0) signePlusImpose = true;
            }
            if ((num === 0) && (chsig !== "") && (chsig.charAt(chsig.length-1) === "0")) return " ";
            g = kernel.pgcd(numabs, denabs);
            num1 = numabs/g;
            den1 = denabs/g;
            positif = (num*den >= 0);
            if (kernel.zero13(den1 - 1)) {
              if (kernel.zero13(num1 - 1) && (chsig !== "") && (chsig.charAt(chsig.length-1) === "1")) {
                if (positif) return  signePlusImpose ? " + " : " ";
                else return " - ";
              }
              res = kernel.chaineNombre(num1, 0);
            }
            else res = " \\frac{" + kernel.chaineNombre(num1, 0) + "}{" + kernel.chaineNombre(den1, 0) + "} ";
            if (positif && signePlusImpose) res = " +" + res;
            else if (!positif) res = " -" + res;
            return res;
          }
        }
      }
    }
   }
  return res;
};
/**
 * Fonction traitant les codes \If permettant d'avoir un affichage conditionnel
 * La syntaxe est \If{Test}{Affichage si 1}{AffichageSinon}
 * @param {Array} param : Tableau de chaînes contenant les codes.
 * @returns {String} : La chaîne égale au résultat du test .
 */
CLatex.prototype.traiteCodeIf = function(param) {
  var pValeurNum,num;
  var indfin = this.listeValeursDynamiquesUtilisees.longueur() - 1;
  // Modifié version 4.7.2
  // CValDyn pValeurNum = latex.listeValeursDynamiquesUtilisees.pointeurValeurOuFonction(nomvaleur,indfin);
  pValeurNum = this.listeValeursDynamiquesUtilisees.pointeurParNatureCalcul
      (NatCal.NTteValPourComDynSaufFonc, param[0], indfin);
  if (pValeurNum === null) return CLatex.errorIf;
  if (pValeurNum.existe) {
    if (pValeurNum.getNatureCalcul() === NatCal.NCalculComplexe) {
      var z = new Complexe();
      pValeurNum.rendValeurComplexe(z);
      if ((z.x === 1) && (z.y === 0)) return param[1]; else return param[2];
    }
    else {
      if (pValeurNum.estDeNatureCalcul(NatCal.NTteValRPourComDyn)) {
        num = pValeurNum.rendValeur();
        if (num === 1) return param[1]; else return param[2];
      }
      else return param[2];
    }
  }
  else return param[2];
};
/**
 * Fonction traitant les codes \For permettant d'obtenir le codeLaTex d'un calcul ou une fonction.
 * Les calculs ou variables utilisés dans la fonction ne sont pas remplacés par leur valeur.
 * La syntaxe est \For{a} où a est le nom d'une calcul ou d'une fonction.
 * @param {String} param : La chaîne à traiter.
 * @returns {String} : La chaîne contenant le code LaTeX.
 */
CLatex.prototype.traiteCodeFor = function(param) {
  var indfin,calcul,varFor;
  indfin = this.listeValeursDynamiquesUtilisees.longueur() - 1;
  calcul = this.listeValeursDynamiquesUtilisees.pointeurParNatureCalcul
      (NatCal.NTteValPourComDyn, param,indfin);
  if (calcul !== null) {
    varFor = calcul.variableFormelle();
    return calcul.calcul.chaineLatexSansPar(varFor);
  }
  else return CLatex.errorFor;
};
/**
 * Fonction traitant les codes \Calc permettant d'afficher la formule d'un calcul ou une fonction
 * Cette formule est rénvoyée telle qu'elle serait écrite (avec les signes * de multiplication).
 * Le code renvoyé n'est pas un code LaTeX.
 * Les calculs ou variables utilisés dans la formule ne sont pas remplacés par leur valeur.
 * La syntaxe est \Calc{a} où a est le nom d'une calcul ou d'une fonction.
 * @param {String} param : La chaîne contenant les codes.
 * @returns {String} : La chaîne contenant la formule.
 */
CLatex.prototype.traiteCodeCalc = function(param) {
  var indfin,calcul,varFor,nbv,k,chret,stb,i;
  indfin = this.listeValeursDynamiquesUtilisees.longueur() - 1;
  calcul = this.listeValeursDynamiquesUtilisees.pointeurParNatureCalcul
      (NatCal.NTteValPourComDyn,param,indfin);
  if (calcul !== null) {
    varFor = null;
    if (calcul.estDeNatureCalcul(Nat.or(NatCal.NFonction, NatCal.NFonctionComplexe))) {
      varFor = new Array(1);
      varFor[0] = calcul.chaineVariable;
    }
    else {
      if (calcul.estDeNatureCalcul(Nat.or(NatCal.NFonction2Var, NatCal.NFonction3Var,
        NatCal.NFonctionComplexe2Var, NatCal.NFonctionComplexe3Var))) {
        nbv = calcul.nbVar;
        varFor = new Array(nbv);
        for (k = 0; k < nbv; k++) varFor[k] = calcul.chaineVariable[k];
      }
    }
    // chret = "\\text{" + calcul.calcul.chaineCalculSansPar(varFor) + "}"; //.Modifié version 4.9.2.3
    chret = calcul.calcul.chaineCalculSansPar(varFor);
    // Il faut remplacer les caractères ^ par \^{}
    stb = chret;
    for (i = 0; i < stb.length; i++) {
      if (stb.charAt(i) === '^') {
        // stb.replace(i, i+1, "\\;\\^{}\\;");
        // stb = stb.substring(0, i-1) + "\\;\\^{}\\;" + stb.substring(i+1); //.Modifié version 4.9.2.3
        stb = stb.substring(0, i) + " \\;\\widehat{\\;\\;}\\; " + stb.substring(i+1);
        i = i + 8;
      }
    }
    return stb.toString();
  }
  else return CLatex.errorCalc;
};
/**
 * Fonction traitant les codes \Calc permettant d'afficher la formule d'un calcul ou une fonction
 * Cette formule est rénvoyée telle qu'elle serait écrite (avec les signes * de multiplication).
 * Le code renvoyé n'est pas un code LaTeX.
 * Les calculs ou variables utilisés dans la formule sont remplacés par leur valeur.
 * L'expression du calcul est simplifiée le plus possible (par exemple les termes nuls d'une somme
 * ne sont pas écrits, 1*x est remplacé par x).
 * La syntaxe est \Calc{a} où a est le nom d'une calcul ou d'une fonction.
 * @param {String} param : La chaîne contenant les codes.
 * @returns {String} : La chaîne contenant la formule.
 */
CLatex.prototype.traiteCodeCalcSimp = function(param) {
  var indfin,calcul,varFor,chret,stb,i;
  indfin = this.listeValeursDynamiquesUtilisees.longueur() - 1;
  calcul = this.listeValeursDynamiquesUtilisees.pointeurParNatureCalcul
      (NatCal.NTteValPourComDyn, param.toString(),indfin);
  if (calcul !== null) {
    varFor = calcul.variableFormelle();
    chret = "\\text{" + calcul.calcul.calculAvecValeursRemplacees().
        calculNormalise(true, false, false).chaineCalculSansPar(varFor) + "}";
    // Il faut remplacer les caractères ^ par \^{}
    stb = chret;
    for (i = 0; i < stb.length; i++) {
      if (stb.charAt(i) === '^') {
        stb = stb.substring(0, i-1) + "\\;\\^{}\\;" + stb.substring(i+1);
        i = i + 8;
      }
    }
    return stb.toString();
  }
  else return CLatex.errorCalcSimp;
};
/**
 * Fonction traitant les codes \For permettant d'obtenir le codeLaTex d'un calcul ou une fonction.
 * Les calculs ou variables utilisés dans la fonction sont pas remplacés par leur valeur.
 * L'expression du calcul est simplifiée le plus possible (par exemple les termes nuls d'une somme
 * ne sont pas écrits, 1*x est remplacé par x).
 * La syntaxe est \For{a} où a est le nom d'une calcul ou d'une fonction.
 * @param {String} param : La chaîne à traiter.
 * @returns {String} : La chaîne contenant le code LaTeX.
 */

CLatex.prototype.traiteCodeForSimp = function(param) {
  var indfin,calcul,varFor;
  indfin = this.listeValeursDynamiquesUtilisees.longueur() - 1;
  calcul =this.listeValeursDynamiquesUtilisees.pointeurParNatureCalcul
      (NatCal.NTteValPourComDyn, param,indfin);
  if (calcul !== null) {
    varFor = calcul.variableFormelle();
    return calcul.calcul.calculAvecValeursRemplacees().calculNormalise(true, false, false).chaineLatexSansPar(varFor);
  }
  else return CLatex.errorForSimp;

};
/**
 * Fonction renvoyant l'indice de l'accolade fermante acorrespondant à celle d'indice
 * pdebut dans chaine.
 * @param {String} chaine : La chaine dans laquelle se fait la recherche. Le caractère
 * d'indice pdebut doit être un {
 * @param {Integer} pdebut : L'indice du premier caractère de la recherche qui pointe sur une {
 * @returns {Integer} : L'indice de la parenthèse fermante ou -1 s'il n'y en a pas.
 */
CLatex.accoladeFermante = function(chaine, pdebut) {
  var p;
  var ch;
  var somme;

  somme = 1;
  p = pdebut + 1;
  while (p < chaine.length) {
    ch = chaine.charAt(p);
    if (ch === '{')
      somme++;
    else {
      if (ch === '}')
        somme--;
    }
    if (somme === 0) break;
    p++;
  }
  if (somme === 0) return p;
  else return - 1; // On renvoie -1 si pas trouvé
};
/**
 * Fonction écrite pour pallier le mauvais rendu des accents en svg par MathJax.
 * Remplace les caractères accentués utilsiés dans des \text par des codes LaTeX.
 * @param {String} ch : La chaîne à traiter.
 * @returns {String} : La cahîne traitée.
 */
CLatex.traiteAccents = function(ch) {
  var indpf,st,i,car,car2,bool,ind;
  var a = "éèàùâêôî";
  var b = ["acute","grave","grave","grave","hat","hat","hat","hat"];
  var c = "eeauaeoi";
  bool = false;
  for (i = 0; i < a.length;i++) bool = bool || (ch.indexOf(a.charAt(i)) !== 0);
  if (!bool || ch.indexOf("\\text{") === -1) return ch;
  var res = "";
  var inddeb = 0;
  while (((ind = ch.indexOf("\\text{", inddeb)) !== -1) && (inddeb < ch.length)) {
    indpf = CLatex.accoladeFermante(ch, ind+ 6);
    if (indpf === -1) break;
    res = res + ch.substring(inddeb, ind);
    st = ch.substring(ind+6, indpf + 1);
    for (i = 0; i < a.length;i++) {
      car = a.charAt(i);
      car2 = c.charAt(i);
      if (st.charAt(0) === car) st = "\\"+b[i]+"{"+car2+"}" +"\\text{"+st.substring(1);
      if (st.charAt(st.length-1) === car)
        st = st.substring(0,st.length-1)+"\\"+b[i]+"{"+car2+"}";
      st = st.replace(new RegExp(car,"g"), "}\\"+b[i]+"{"+car2+"} \\text{");
    }
    // Impossible de traite le c cédille
    st = st.replace(new RegExp("ç","g"),"c");
    bool = false;
    for (i = 0; i < a.length;i++) bool = bool || (st.indexOf("\\"+b[i]) === 0);
    if (bool) res = res + st;
    else res = res+"\\text{"+st;
    inddeb = indpf + 1;
  }
  if (inddeb < ch.length) res = res + ch.substring(inddeb);
  res = res.replace(/\\text{}/g, " ");
  return res;
};

CLatex.prototype.read = function(inps, list){
  CCommentaire.prototype.read.call(this, inps, list);
  this.chaineCommentaire = CLatex.traiteAccents(this.chaineCommentaire);
};
/**
 * Fonction remplaçant dans this.commentairre les appels dynamiques de calcul,
 * fonction ou variable nommé ancienNom par nouveauNom.
 * @param {String} ancienNom
 * @param {String} nouveauNom
 * @returns {Boolean} : la chaîne traitée.
 */
CLatex.prototype.remplaceNomValeurDynamique = function(ancienNom, nouveauNom) {
  // On supprime d'abord tous les caractères espace suivant un { ou une virgule ou précédant une accolade
  var buffer = this.chaineCommentaire;
  var chaineInitiale = buffer;
  /* Inutile avec des expressiosn régulières
  var ind;
  while((ind = buffer.indexOf("\\Val{ ", 0)) !== - 1) {
    buffer.deleteCharAt(ind+5);
  }
  while((ind = buffer.indexOf("\\For{ ", 0)) !== - 1) {
    buffer.deleteCharAt(ind+5);
  }
  while((ind = buffer.indexOf("\\ForSimp{ ", 0)) !== - 1) {
    buffer.deleteCharAt(ind+9);
  }
  while((ind = buffer.indexOf("\\Calc{ ", 0)) !== - 1) {
    buffer.deleteCharAt(ind+6);
  }
  while((ind = buffer.indexOf("\\CalcSimp{ ", 0)) !== - 1) {
    buffer.deleteCharAt(ind+10);
  }
  while((ind = buffer.indexOf("\\FracRed{ ", 0)) !== - 1) {
    buffer.deleteCharAt(ind+9);
  }
  */
  var ch1 = "\\\\Val\\{[ ]*" + ancienNom + "[ ]*\\}";
  var ch2 = "\\Val{" + nouveauNom + "}";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  /*
  ch1 = "\\\\Val{" + ancienNom + " ";
  ch2 = "\\Val{" + nouveauNom + " ";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  */
  ch1 = "\\\\Val{[ ]*" + ancienNom + "[ ]*,";
  ch2 = "\\Val{" + nouveauNom + ",";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  ch1 = "\\\\For{[ ]*" + ancienNom + "[ ]*\\}";
  ch2 = "\\For{" + nouveauNom + "}";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  /*
  ch1 = "\\\\For{" + ancienNom + " ";
  ch2 = "\\For{" + nouveauNom + " ";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  */
  ch1 = "\\\\ForSimp{[ ]*" + ancienNom + "[ ]*\\}";
  ch2 = "\\ForSimp{" + nouveauNom + "}";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  /*
  ch1 = "\\\\ForSimp{" + ancienNom + " ";
  ch2 = "\\ForSimp{" + nouveauNom + " ";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  */
  ch1 = "\\\\Calc{[ ]*" + ancienNom + "[ ]*\\}";
  ch2 = "\\Calc{" + nouveauNom + "}";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  /*
  ch1 = "\\\\Calc{" + ancienNom + " ";
  ch2 = "\\Calc{" + nouveauNom + " ";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  */
  ch1 = "\\\\CalcSimp{[ ]*" + ancienNom + "[ ]*\\}";
  ch2 = "\\CalcSimp{" + nouveauNom + "}";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  /*
  ch1 = "\\\\CalcSimp{" + ancienNom + " ";
  ch2 = "\\CalcSimp{" + nouveauNom + " ";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  */
  ch1 = "\\\\FracRed{[ ]*" + ancienNom + "[ ]*,";
  ch2 = "\\FracRed{" + nouveauNom + ",";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  /*
  ch1 = "\\\\FracRed{" + ancienNom + " ";
  ch2 = "\\FracRed{" + nouveauNom + " ";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  */
  ch1 = "\\\\If{[ ]*" + ancienNom + "[ ]*\\}";
  ch2 = "\\If{" + nouveauNom + "}";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  /*
  ch1 = "\\\\If{" + ancienNom + " ";
  ch2 = "\\If{" + nouveauNom + " ";
  buffer = buffer.replace(new RegExp(ch1,"g"), ch2);
  */
  this.chaineCommentaire = buffer;
  this.remplaceNomValeurDynamiqueApresVirgule("\\FracRed{", ancienNom, nouveauNom);
  return (chaineInitiale !== this.chaineCommentaire);
};
/**
 * Fonction recherchant dans les codes spéciaux utilisant une virgue ancienNom et le replaçant par nouveauNom.
 * @param {String} code : contient  \FracRed qui est pour le moment le seul code avec virgules
 * dont le deuxième argument peut être le nom d'un calcul ou une variable.
 * @param {String} ancienNom : Le nom de calcul, variable ou fonction à remplacer.
 * @param {String} nouveauNom : Le nom du calcul, variable ou fonction qui remplace.
 * @returns {undefined}
 */
CLatex.prototype.remplaceNomValeurDynamiqueApresVirgule = function(code, ancienNom, nouveauNom) {
  var ind = 0;
  while((ind = this.chaineCommentaire.indexOf(code, ind)) !== -1) {
    // On recherche la première accolade fermante
    var indacf = this.chaineCommentaire.indexOf('}', ind + code.length);
    if (indacf !== -1) {
      var left = this.chaineCommentaire.substring(0, ind + code.length - 1);
      var right = this.chaineCommentaire.substring(indacf + 1);
      var mid = this.chaineCommentaire.substring(ind + code.length - 1, indacf + 1);
      var ch1 = "," + ancienNom + " ";
      var ch2 = "," + nouveauNom + " ";
      mid = mid.replace(new RegExp(ch1,"g"), ch2);
      ch1 = "," + ancienNom + ",";
      ch2 = "," + nouveauNom + ",";
      mid = mid.replace(new RegExp(ch1,"g"), ch2);
      ch1 = " " + ancienNom + ",";
      ch2 = " " + nouveauNom + ",";
      mid = mid.replace(new RegExp(ch1,"g"), ch2);
      ch1 = " " + ancienNom + " ";
      ch2 = " " + nouveauNom + " ";
      mid = mid.replace(new RegExp(ch1,"g"), ch2);
      ch1 = " " + ancienNom + "}";
      ch2 = " " + nouveauNom + "}";
      mid = mid.replace(new RegExp(ch1,"g"), ch2);
      ch1 = "," + ancienNom + "}";
      ch2 = "," + nouveauNom + "}";
      mid = mid.replace(new RegExp(ch1,"g"), ch2);
      this.chaineCommentaire = left + mid + right;
    }
    ind = ind + code.length - 1;
  }
};
CLatex.prototype.chaineDesignation = function() {
  return "desLatex";
};
